import React from 'react';
import { X } from 'lucide-react';

const ScreenerDetailModal = ({ isOpen, onClose, title, data }) => {
    if (!isOpen) return null;

    const handleDownload = () => {
        if (!data || data.length === 0) return;

        // 1. Convert data to CSV
        const headers = ["Date", "Symbol", "Close Price", "HPCHU Signal", "HPCHU Value", "LPCLD Signal", "LPCLD Value"];
        const csvRows = [headers.join(",")];

        data.forEach(row => {
            const dateStr = new Date(row.date).toISOString().split('T')[0];
            const values = [
                dateStr,
                row.symbol,
                row.close,
                row.hpchu_signal,
                parseFloat(row.hpchu_val).toFixed(2),
                row.lpcld_signal,
                parseFloat(row.lpcld_val).toFixed(2)
            ];
            csvRows.push(values.join(","));
        });

        const csvContent = csvRows.join("\n");

        // 2. Create Blob and Link
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        // 3. Set filename (sanitize title for filename)
        const filename = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_screener_results.csv`;

        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);

        // 4. Trigger Download and Cleanup
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-[#1e293b] w-full max-w-4xl max-h-[80vh] rounded-xl border border-slate-700 shadow-2xl flex flex-col">
                <div className="flex justify-between items-center px-6 py-4 border-b border-slate-700/50 bg-[#0f172a]/50">
                    <h3 className="text-lg font-bold text-slate-100">{title}</h3>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleDownload}
                            className="flex items-center gap-2 px-3 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 text-xs font-bold uppercase tracking-wider rounded border border-indigo-500/30 transition-colors"
                            title="Download CSV"
                        >
                            <span className="hidden sm:inline">Download CSV</span>
                        </button>
                        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto custom-scrollbar p-0">
                    <table className="w-full text-sm border-collapse">
                        <thead className="sticky top-0 bg-[#0f172a] text-slate-400 text-xs uppercase font-semibold">
                            <tr>
                                <th className="py-3 px-4 text-center w-12">#</th>
                                <th className="py-3 px-4 text-left">Date</th>
                                <th className="py-3 px-4 text-left">Symbol</th>
                                <th className="py-3 px-4 text-right">Close Price</th>
                                <th className="py-3 px-4 text-center">HPCHU Sig</th>
                                <th className="py-3 px-4 text-right">HPCHU Val</th>
                                <th className="py-3 px-4 text-center">LPCLD Sig</th>
                                <th className="py-3 px-4 text-right">LPCLD Val</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/30">
                            {data.map((row, idx) => (
                                <tr key={idx} className="hover:bg-slate-700/20 transition-colors">
                                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-xs">{idx + 1}</td>
                                    <td className="py-3 px-4 text-left text-slate-400 font-mono text-xs whitespace-nowrap">
                                        {new Date(row.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                    </td>
                                    <td className="py-3 px-4 font-medium text-indigo-400">{row.symbol}</td>
                                    <td className="py-3 px-4 text-right font-mono text-slate-300">{row.close}</td>
                                    <td className={`py-3 px-4 text-center font-bold ${row.hpchu_signal === 1 ? 'text-emerald-400' : 'text-slate-600'}`}>
                                        {row.hpchu_signal}
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono text-emerald-400">{parseFloat(row.hpchu_val).toFixed(2)}</td>
                                    <td className={`py-3 px-4 text-center font-bold ${row.lpcld_signal === 1 ? 'text-rose-400' : 'text-slate-600'}`}>
                                        {row.lpcld_signal}
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono text-rose-400">{parseFloat(row.lpcld_val).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.length === 0 && (
                        <div className="p-8 text-center text-slate-500">No stocks found matching criteria.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScreenerDetailModal;
